import type { KeyboardEvent } from "react";

import { BTStatusBadge } from "@btnet/core-components/BTStatusBadge";
import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";

import { CurrencyDisplay } from "commonComponents/financial/CurrencyDisplay/CurrencyDisplay";

import { toneToDisplayType } from "../searchDialog.types";
import { CountPill } from "./CountPill";
import type { ISearchDialogRow } from "./searchDialogRow.types";

export interface ISearchDialogRowProps {
    row: ISearchDialogRow;
    isHighlighted: boolean;
    /** Set by the list when the row participates in combobox active-descendant. */
    elementId?: string;
    /**
     * Applied by the container, not the row — see {@link ISearchDialogRow}.
     * "option" inside the combobox listbox, where the input drives the keyboard;
     * "button" anywhere else, where nothing does and the row must take focus.
     */
    role?: "option" | "button";
    /** Moves the keyboard selection here, so pointer and keyboard agree. */
    onHover?: () => void;
    /**
     * A one-line search prediction: regular weight, so the bold part (what the
     * prediction adds to the typed text) stands out, and a tighter row.
     */
    compact?: boolean;
}

/**
 * One row in the dialog, in any section. Absent fields render as nothing rather
 * than a placeholder — a record with no amount is the normal case.
 */
export function SearchDialogRow({
    row,
    isHighlighted,
    elementId,
    role,
    onHover,
    compact = false,
}: ISearchDialogRowProps) {
    const { icon, title, subtitle, amount, status, trailingLabel, onSelect, count } = row;
    const isInteractive = onSelect !== null;
    // An unroutable row outside a listbox stays inert rather than a dead button.
    const isButton = role === "button" && onSelect !== null;
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (onSelect !== null && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            // A held key opens the record once, not once per repeat.
            if (!event.repeat) {
                onSelect("keyboard");
            }
        }
    };
    // The record's kind reads as the tail of the subtitle ("Pine Hill • Bill"),
    // so a list mixing kinds names each one without a separate column.
    const detail = [subtitle, trailingLabel].filter((part) => part !== null).join(" • ");

    return (
        <div
            id={elementId}
            role={role === "option" || isButton ? role : undefined}
            aria-selected={role === "option" ? isHighlighted : undefined}
            tabIndex={isButton ? 0 : undefined}
            // Not onSelect itself, which would be handed the click event.
            onClick={onSelect === null ? undefined : () => onSelect("click")}
            onKeyDown={isButton ? handleKeyDown : undefined}
            // mousemove, so the row under a stationary cursor reclaims the
            // selection after a keyboard move.
            onMouseMove={onHover}
            className={cn(
                // The edge marks the selection by shape as well as fill: the
                // fill alone is barely distinguishable from the white around it.
                // The edge plus pl-1 keeps the text on the section label's px-2 line.
                "flex items-center gap-1 rounded-lg border-l-4 py-1 pl-1 pr-2",
                isHighlighted
                    ? "border-l-surface-primary bg-surface-selected"
                    : "border-l-transparent",
                isInteractive ? "cursor-pointer" : "cursor-default",
                // Keep the selected fill under the pointer.
                isInteractive &&
                    (isHighlighted
                        ? "hover:bg-surface-selected-hover"
                        : "hover:bg-surface-default-hover"),
                isButton &&
                    "outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-50",
            )}
            data-testid="search-dialog-row"
            data-row-key={row.key}
            data-row-kind={row.kind}
        >
            {icon !== null && (
                <span className="flex shrink-0 p-1.5 text-fg-secondary" aria-hidden="true">
                    {icon}
                </span>
            )}
            <span className="flex min-w-0 flex-1 flex-col">
                <BTText
                    tag="span"
                    className={cn(
                        "truncate text-lg text-fg-default",
                        compact ? "font-normal" : "font-medium",
                    )}
                >
                    {title}
                </BTText>
                {detail.length > 0 && (
                    <BTText size="normal-md" tag="span" className="truncate text-fg-secondary">
                        {detail}
                    </BTText>
                )}
            </span>
            {amount !== null && (
                <BTText size="normal-sm" tag="span" className="shrink-0 text-fg-default">
                    {/* Never a hardcoded symbol: CurrencyDisplay reads the builder's
                        locale from context itself. Whole dollars — cents are
                        noise when scanning a list, and the record's own page
                        has the exact figure. */}
                    <CurrencyDisplay value={amount} decimalScale={0} />
                </BTText>
            )}
            {count !== undefined && count !== null && (
                <CountPill value={count.value} tone={count.tone} testid="search-dialog-row-count" />
            )}
            {status !== null && (
                <span className="shrink-0">
                    <BTStatusBadge
                        testid="search-dialog-row-status"
                        displayType={toneToDisplayType(status.tone)}
                        text={status.text}
                    />
                </span>
            )}
        </div>
    );
}
